import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center border font-medium w-fit shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow]",
  {
    variants: {
      size: {
        legacy: "rounded-md px-2 py-0.5 text-xs whitespace-nowrap [&>svg]:size-3 overflow-hidden",
        chip: "min-h-chip min-w-0 max-w-full px-1.5 py-0 rounded-control text-chip normal-case whitespace-normal [overflow-wrap:anywhere] [&>svg]:icon-sm [&>svg]:shrink-0",
      },
      variant: {
        danger: "border-danger-edge bg-danger-soft text-danger-on-soft",
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-ink [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-edge text-ink [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        neutral: "border-edge bg-raised text-ink-secondary",
        success: "border-success-edge bg-success-soft text-success-on-soft",
        warning: "border-warning-edge bg-warning-soft text-warning-on-soft",
        info: "border-info-edge bg-info-soft text-info-on-soft",
        brand: "border-transparent bg-brand-muted text-brand",
        ai: "border-ai-border bg-ai-fill text-ai-text",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "legacy",
    },
  }
)

// STANDARD 2.6: document the aliases while preserving legacy rendering.
export const badgeVariantAliases = { default: 'brand', secondary: 'neutral', destructive: 'danger', outline: 'neutral' } as const

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
